import Vue from "vue";
import DatePicker from "vue2-datepicker";
import moment from "moment";
import Paginate from "@/components/widgets/pagination.vue";

const STATUS_OPTIONS = [
  { value: 1, text: "รอผู้ผลิตคอนเฟิร์ม" },
  { value: 2, text: "คอนเฟิร์มแล้ว (พร้อมออกใบผลิต)" },
  { value: 4, text: "ออกใบผลิตแล้ว" },
  { value: 6, text: "ยกเลิก" },
];

export default {
  components: { DatePicker, Paginate },

  data() {
    return {
      sum: { all: 0, month: 0, year: 0 },
      totalItem: 0,

      keyword: "",
      keyword_status: null,
      keyword_side: null,
      keyword_startdate: null,
      keyword_enddate: null,

      role: "",
      branchShortname: "",
      branch: "",

      loading: false,
      currentPage: 1,
      currentData: {},

      statusOptions: STATUS_OPTIONS,

      items: [],
      window: { width: window.innerWidth },
    };
  },

  computed: {
    // เสมียนที่เข้ามาเพราะฝั่งรับผลิต (ไม่ใช่สาขาที่เปิดให้สั่งโป๊ว — ตรงกับ backend App\Support\BranchClerk)
    // เห็นเฉพาะใบที่สาขาตัวเองรับผลิต สร้างใบสั่งไม่ได้
    isProducerOnlyClerk() {
      return this.role === "Customer assistant" && !["RY"].includes(this.branchShortname);
    },
    // เห็นราคา: เฉพาะระดับ ผจก. (Admin / ผจก.สาขา)
    // backend strip grand_total ให้ role อื่นแล้ว ถ้าโชว์คอลัมน์จะเป็นช่องว่างเปล่า ๆ
    canSeePricing() {
      return this.role === "Admin" || this.role === "Branch Admin";
    },

    // fields เป็น computed เพราะคอลัมน์ราคาขึ้นกับ role ซึ่งรู้หลัง getRole()
    fields() {
      const cols = [
        { key: "document_no", label: "เลขที่เอกสาร", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2" },
        { key: "owner_branch", label: "ผู้ว่าจ้าง", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2" },
        { key: "producer_branch", label: "ผู้รับผลิต", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2" },
        { key: "creator.name", label: "ผู้จัดทำ", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2" },
        { key: "status", label: "สถานะ", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2" },
      ];
      if (this.canSeePricing) {
        cols.push({
          key: "grand_total",
          label: "ยอดรวม",
          thClass: "font-weight-bold text-xs-1/2 text-right",
          tdClass: "td-top text-xs-1/2 text-right",
        });
      }
      cols.push({ key: "action", label: "#", thClass: "font-weight-bold text-xs-1/2", tdClass: "td-top text-xs-1/2", class: "text-right" });
      return cols;
    },
  },

  methods: {
    toThaiDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" });
    },

    toMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return "0.00";
      return n.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    statusText(s) {
      const found = STATUS_OPTIONS.find((x) => x.value === Number(s));
      return found ? found.text : s;
    },

    statusVariant(s) {
      const n = Number(s);
      if (n === 1) return "secondary";   // waiting producer confirm
      if (n === 2) return "info";        // confirmed, ready to issue production
      if (n === 4) return "success";     // production issued
      if (n === 6) return "danger";      // cancelled
      return "light";
    },

    onResetFilters() {
      this.keyword = "";
      this.keyword_status = null;
      this.keyword_side = null;
      this.keyword_startdate = null;
      this.keyword_enddate = null;
      this.onInitData(1);
    },

    async getRole() {
      try {
        const res = await this.$store.dispatch("Auth/getRole", {}, { root: true });
        this.role = res?.data?.role || "";
        this.branch = res?.data?.branch_id || "";
        this.branchShortname = (res?.data?.detail && res.data.detail.shortname) || "";
        // เสมียนฝั่งรับผลิต: backend บังคับมุมมอง "ผู้รับผลิต" อยู่แล้ว — ตั้งให้ตรงกันบนจอ
        if (this.isProducerOnlyClerk) this.keyword_side = "producer";
      } catch (e) {
        this.role = "";
        this.branch = "";
      }
    },

    async onInitData(currentPage = 1) {
      this.currentPage = currentPage;
      this.loading = true;
      try {
        const qs = new URLSearchParams();
        qs.set("page", String(currentPage || 1));
        qs.set("keyword", this.keyword || "");
        qs.set("keyword_status", this.keyword_status ?? "");
        qs.set("keyword_side", this.keyword_side ?? "");
        qs.set("keyword_startdate", this.keyword_startdate ? moment(this.keyword_startdate).format("YYYY-MM-DD") : "");
        qs.set("keyword_enddate", this.keyword_enddate ? moment(this.keyword_enddate).format("YYYY-MM-DD") : "");

        const url = `${Vue.prototype.$host}/api/v1/document/powngan?${qs.toString()}`;
        const result = await Vue.prototype.$axios.get(url);

        const payload = result?.data || {};
        const paginator = Array.isArray(payload?.data) ? payload : payload?.data;

        // ตัวแบ่งหน้า (Paginate) อ่าน to/current_page/last_page จาก paginator โดยตรง
        // เดิมส่ง payload ตัวห่อ ({ data, sum, ... }) ซึ่งไม่มีฟิลด์พวกนี้ → ปุ่มแบ่งหน้าไม่ขึ้น เห็นแค่ 20 ใบแรก
        this.currentData = paginator || {};
        this.items = paginator?.data || [];
        this.totalItem = paginator?.total || 0;

        this.sum.all = Number(payload?.sum || 0);
        this.sum.month = Number(payload?.sum_month || 0);
        this.sum.year = Number(payload?.sum_year || 0);
      } catch (error) {
        this.items = [];
        this.totalItem = 0;
        this.currentData = {};
        this.sum = { all: 0, month: 0, year: 0 };
        const msg = error?.response?.data?.message || error?.message || "ไม่สามารถโหลดข้อมูลได้";
        if (typeof this.onExceptionHandler === "function") this.onExceptionHandler(msg);
        else console.error(msg);
      } finally {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.$nextTick(() => this.onInitData(this.currentPage));
    this.getRole();
  },
};
